import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { format, formatDistance, parseISO } from 'date-fns';
import { Decimal } from 'decimal.js';
import { formatDateTimeWithShortMonth } from '~/utils/date-formatting';
const ENTITY_REFS = new Set([
    'client',
    'agreement'
]);
const entityRefOf = (column)=>(column === null || column === void 0 ? void 0 : column.entity_ref) != null && ENTITY_REFS.has(column.entity_ref) ? column.entity_ref : undefined;
export const ANALYST_TABLE_INLINE_ROW_CAP = 15;
export const ANALYST_CSV_FILENAME_STEM = 'anchor_analyst_export';
const isValidDate = (date)=>!Number.isNaN(date.getTime());
const alignmentForColumnType = (type)=>type === 'number' ? 'right' : 'left';
const RIGHT_ALIGNED_FORMATS = new Set([
    'currency',
    'percent'
]);
export const alignmentForColumn = (column)=>(column === null || column === void 0 ? void 0 : column.format) != null && RIGHT_ALIGNED_FORMATS.has(column.format) ? 'right' : alignmentForColumnType(column === null || column === void 0 ? void 0 : column.type);
const toFiniteDecimal = (cell)=>{
    try {
        const decimal = new Decimal(cell);
        return decimal.isFinite() ? decimal : null;
    } catch (unused) {
        return null;
    }
};
const renderCurrency = (cell)=>{
    const decimal = toFiniteDecimal(cell);
    if (decimal == null) return String(cell);
    const [intPart, decPart] = decimal.abs().toFixed(2).split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return "".concat(decimal.isNegative() ? '-' : '', "$").concat(grouped, ".").concat(decPart);
};
const renderPercent = (cell)=>{
    const decimal = toFiniteDecimal(cell);
    return decimal == null ? String(cell) : "".concat(decimal.times(100).toFixed(1), "%");
};
const renderRatio = (cell)=>{
    const decimal = toFiniteDecimal(cell);
    return decimal == null ? String(cell) : "1:".concat(decimal.toFixed(2));
};
const renderUtcCalendar = (cell, options)=>{
    const parsed = parseISO(String(cell));
    return isValidDate(parsed) ? parsed.toLocaleDateString('en-US', _object_spread_props(_object_spread({}, options), {
        timeZone: 'UTC'
    })) : String(cell);
};
const renderDate = (cell)=>renderUtcCalendar(cell, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
const renderMonth = (cell)=>renderUtcCalendar(cell, {
        year: 'numeric',
        month: 'short'
    });
const renderDateTime = (cell)=>{
    const parsed = parseISO(String(cell));
    return isValidDate(parsed) ? formatDateTimeWithShortMonth(parsed) : String(cell);
};
const formatByType = (cell, type)=>{
    if (type === 'boolean') return {
        kind: 'boolean',
        value: Boolean(cell)
    };
    if (type === 'timestamp') return {
        kind: 'text',
        text: renderDateTime(cell)
    };
    if (type === 'json' && typeof cell !== 'string') return {
        kind: 'text',
        text: JSON.stringify(cell)
    };
    return {
        kind: 'text',
        text: String(cell)
    };
};
const resolutionToRenderable = (resolution, cell)=>{
    if (resolution.status === 'resolved') return {
        kind: 'text',
        text: resolution.name
    };
    if (resolution.status === 'empty') return {
        kind: 'null'
    };
    return {
        kind: 'text',
        text: String(cell)
    };
};
export const formatCell = (cell, column, resolve)=>{
    if (cell == null) return {
        kind: 'null'
    };
    const entityRef = entityRefOf(column);
    if (resolve != null && entityRef != null) return resolutionToRenderable(resolve(entityRef, cell), cell);
    switch(column === null || column === void 0 ? void 0 : column.format){
        case 'currency':
            return {
                kind: 'text',
                text: renderCurrency(cell)
            };
        case 'percent':
            return {
                kind: 'text',
                text: renderPercent(cell)
            };
        case 'ratio':
            return {
                kind: 'text',
                text: renderRatio(cell)
            };
        case 'date':
            return {
                kind: 'text',
                text: renderDate(cell)
            };
        case 'month':
            return {
                kind: 'text',
                text: renderMonth(cell)
            };
        case 'datetime':
            return {
                kind: 'text',
                text: renderDateTime(cell)
            };
        default:
            return formatByType(cell, column === null || column === void 0 ? void 0 : column.type);
    }
};
export const renderCellText = (renderable, labels)=>{
    if (renderable.kind === 'null') return labels.nullPlaceholder;
    if (renderable.kind === 'boolean') return renderable.value ? labels.booleanYes : labels.booleanNo;
    return renderable.text;
};
export const displayedRows = function displayedRows(rows) {
    let cap = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : ANALYST_TABLE_INLINE_ROW_CAP;
    return rows.slice(0, cap);
};
export const displayedCount = function displayedCount(rows) {
    let cap = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : ANALYST_TABLE_INLINE_ROW_CAP;
    return Math.min(rows.length, cap);
};
export const shouldShowTruncationBanner = function shouldShowTruncationBanner(rows, truncated) {
    let cap = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : ANALYST_TABLE_INLINE_ROW_CAP;
    return rows.length > cap || truncated;
};
export const freshnessLabel = (lastSyncedAtIso, now)=>{
    if (lastSyncedAtIso == null) return {
        kind: 'unavailable'
    };
    const parsed = parseISO(lastSyncedAtIso);
    if (!isValidDate(parsed)) return {
        kind: 'unavailable'
    };
    return {
        kind: 'synced',
        relative: formatDistance(parsed, now, {
            addSuffix: true
        })
    };
};
const CSV_CONTROL_PREFIXES = [
    '\t',
    '\r'
];
const CSV_FORMULA_PREFIXES = [
    '=',
    '+',
    '-',
    '@'
];
const isFormulaInjectionRisk = (value)=>{
    const first = value[0];
    return CSV_CONTROL_PREFIXES.includes(first) || CSV_FORMULA_PREFIXES.includes(first) && !Number.isFinite(Number(value));
};
export const cellToCsvValue = (cell)=>{
    if (cell == null) return '';
    if (typeof cell === 'number') return String(cell);
    const value = String(cell);
    return isFormulaInjectionRisk(value) ? "'".concat(value) : value;
};
const csvNameValue = (resolution)=>resolution.status === 'resolved' ? cellToCsvValue(resolution.name) : '';
export const toCsvRows = function toCsvRows(columns, rows, resolve) {
    let nameColumnSuffix = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : '';
    const entityRefAt = (index)=>resolve != null ? entityRefOf(columns[index]) : undefined;
    const header = columns.flatMap((column, index)=>entityRefAt(index) != null ? [
            column.name,
            "".concat(column.name).concat(nameColumnSuffix)
        ] : [
            column.name
        ]);
    const body = rows.map((row)=>row.flatMap((cell, index)=>{
            const entityRef = entityRefAt(index);
            if (resolve == null || entityRef == null) return [
                cellToCsvValue(cell)
            ];
            return [
                cellToCsvValue(cell),
                cell == null ? '' : csvNameValue(resolve(entityRef, cell))
            ];
        }));
    return [
        header,
        ...body
    ];
};
export const analystCsvFilename = (now, truncated)=>{
    const base = "".concat(ANALYST_CSV_FILENAME_STEM, "_").concat(format(now, 'yyyy-MM-dd_HHmmss'));
    return truncated ? "".concat(base, "_partial.csv") : "".concat(base, ".csv");
};
